/**
 * Capture-mode controller (E1.5.S1). Owns the client-LOCAL capture presentation
 * state (master toggle, standalone HUD toggle, health-bar sub-flag, camera preset,
 * slow-orbit drift), installs the window-level hotkeys (F4 capture / F3 HUD /
 * `[` `]` preset cycle / `\` slow-orbit — table in capture.ts `CAPTURE_HOTKEYS`,
 * with the same typing guard the Shell uses), drives the
 * Game's camera + sprite hooks, and renders a brief self-fading on-toggle notice.
 *
 * It renders the HUD + Shell with capture-aware visibility props derived from the
 * pure `captureView()` (game/capture.ts), so nothing DOM remains in frame when
 * capture mode is on. Nothing here touches the socket or confers power (AD-124/021).
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import type { Game, GameStatus } from '../game/Game.js';
import {
  CAPTURE_KEYS,
  captureHotkeyAction,
  captureView,
  cyclePresetId,
  presetById,
  readFlag,
  readPresetId,
  writeFlag,
  writePresetId,
} from '../game/capture.js';
import { Hud } from './Hud.js';
import { Shell } from './Shell.js';

/** True when focus is in a text field — hotkeys must not fire while typing (Shell.tsx pattern). */
function isTyping(): boolean {
  const el = document.activeElement;
  return (
    el instanceof HTMLInputElement ||
    el instanceof HTMLTextAreaElement ||
    (el as HTMLElement | null)?.isContentEditable === true
  );
}

export function CaptureController({
  game,
  status,
  onLeave,
}: {
  /** The live Game instance (camera + sprite hooks); null before the world is ready. */
  game: Game | null;
  status: GameStatus;
  onLeave: () => void;
}): JSX.Element {
  const [captureMode, setCaptureMode] = useState<boolean>(() => readFlag(CAPTURE_KEYS.captureMode));
  const [hudHidden, setHudHidden] = useState<boolean>(() => readFlag(CAPTURE_KEYS.hudHidden));
  const [hideHealthBars] = useState<boolean>(() =>
    readFlag(CAPTURE_KEYS.captureHideHealthBars, true),
  );
  const [slowOrbit, setSlowOrbit] = useState<boolean>(() => readFlag(CAPTURE_KEYS.slowOrbit));
  const [presetId, setPresetId] = useState<string>(() => readPresetId());
  const [notice, setNotice] = useState<string>('');
  const noticeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flash = useCallback((text: string) => {
    setNotice(text);
    if (noticeTimer.current) clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(''), 2200);
  }, []);

  const view = captureView({ captureMode, hudHidden, hideHealthBars });

  const toggleCapture = useCallback(() => {
    setCaptureMode((on) => {
      const next = !on;
      writeFlag(CAPTURE_KEYS.captureMode, next);
      flash(next ? 'Capture mode on — F4 to exit' : 'Capture mode off');
      return next;
    });
  }, [flash]);

  const toggleHud = useCallback(() => {
    setHudHidden((h) => {
      const next = !h;
      writeFlag(CAPTURE_KEYS.hudHidden, next);
      flash(next ? 'HUD hidden — F3 to show' : 'HUD shown');
      return next;
    });
  }, [flash]);

  const cyclePreset = useCallback(
    (dir: 1 | -1) => {
      setPresetId((cur) => {
        const next = cyclePresetId(cur, dir);
        writePresetId(next);
        game?.applyCameraPreset(next);
        flash(`Camera: ${presetById(next).label}`);
        return next;
      });
    },
    [flash, game],
  );

  const toggleSlowOrbit = useCallback(() => {
    setSlowOrbit((on) => {
      const next = !on;
      writeFlag(CAPTURE_KEYS.slowOrbit, next);
      // No notice while capturing: this key is tapped MID-SHOT by the studio's
      // pan verb, and a notice would land in the footage.
      if (!captureMode) flash(next ? 'Slow orbit on — \\ to stop' : 'Slow orbit off');
      return next;
    });
  }, [flash, captureMode]);

  // Window-level hotkeys (typing-guarded), matching the Shell listener discipline.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (isTyping()) return;
      const action = captureHotkeyAction(e.key);
      if (action === null) return;
      e.preventDefault();
      if (action === 'toggleCapture') toggleCapture();
      else if (action === 'toggleHud') toggleHud();
      else if (action === 'presetPrev') cyclePreset(-1);
      else if (action === 'presetNext') cyclePreset(1);
      else toggleSlowOrbit();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggleCapture, toggleHud, cyclePreset, toggleSlowOrbit]);

  // Drive the Game's in-scene health-bar visibility from the derived view.
  useEffect(() => {
    game?.setCaptureHealthBars(view.healthBarsVisible);
  }, [game, view.healthBarsVisible]);

  // Drive slow-orbit drift.
  useEffect(() => {
    game?.setSlowOrbit(slowOrbit);
  }, [game, slowOrbit]);

  // Apply the persisted preset once the game is ready (and whenever it changes here).
  useEffect(() => {
    game?.applyCameraPreset(presetId);
  }, [game, presetId]);

  // Clean up the notice timer on unmount.
  useEffect(() => {
    return () => {
      if (noticeTimer.current) clearTimeout(noticeTimer.current);
    };
  }, []);

  return (
    <>
      {/* Always mounted: the HUD's effects own the ability and interact keys, so hiding it
          only stops it drawing (captureView `hudVisible`). */}
      <div className="pxl-hud-host" style={view.hudVisible ? undefined : { display: 'none' }}>
        <Hud status={status} onLeave={onLeave} />
      </div>
      {view.hudHiddenCueVisible ? (
        <button
          type="button"
          className="pxl-hud-hidden-cue"
          data-testid="hud-hidden-cue"
          onClick={toggleHud}
        >
          HUD hidden · press F3 to show
        </button>
      ) : null}
      {view.launcherVisible ? <Shell captureSuppressed={false} /> : <Shell captureSuppressed />}
      {notice ? <div className="pxl-capture-notice">{notice}</div> : null}
    </>
  );
}
